<template>
  <!-- About clients section -->
  <div class="container mx-auto mt-10 sm:mt-20">
    <div class="text-center">
      <h2 class="mb-4 text-3xl font-bold text-ternary-dark dark:text-primary-light">
        Brands We've Worked With
      </h2>
      <p class="text-lg text-gray-600 dark:text-gray-400">
        Trusted by innovative companies and platforms
      </p>
    </div>

    <div class="mt-10 grid grid-cols-1 gap-6 sm:mt-14 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      <div
        v-for="client in clients"
        :key="client.id"
        class="group relative overflow-hidden rounded-2xl bg-card-gradient shadow-soft transition-all duration-500 hover:-translate-y-2 hover:shadow-soft-lg dark:bg-card-gradient-dark"
      >
        <a
          :href="client.url"
          target="_blank"
          rel="noopener noreferrer"
          class="block p-6 text-center"
        >
          <!-- Brand Logo/Icon -->
          <div class="mb-4 flex justify-center">
            <div class="flex size-16 items-center justify-center rounded-full bg-gradient-to-r from-accent/10 to-gradient-end/10 dark:from-accent/20 dark:to-gradient-end/20">
              <component :is="client.icon" class="size-8 text-accent dark:text-accent" />
            </div>
          </div>

          <!-- Brand Name -->
          <h3 class="mb-2 text-lg font-semibold text-ternary-dark transition-colors duration-300 group-hover:text-accent dark:text-ternary-light dark:group-hover:text-accent">
            {{ client.title }}
          </h3>

          <!-- Brand Description -->
          <p class="text-sm text-gray-600 dark:text-gray-400">
            {{ client.description }}
          </p>

          <!-- Bottom border accent -->
          <div class="mx-auto mt-4 h-1 w-0 rounded-full bg-gradient-to-r from-accent to-gradient-end transition-all duration-500 group-hover:w-full" />
        </a>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from '#imports'
import {
  PhCode,
  PhDatabase,
  PhDeviceMobile,
  PhGift,
  PhLightning,
  PhMapPin,
  PhTarget,
  PhUsers,
} from '@phosphor-icons/vue'
import { v4 } from 'uuid'

interface Client {
  id: string
  title: string
  url: string
  description: string
  icon: any
}

const clients = ref<Client[]>([
  {
    id: v4(),
    title: 'YQueue',
    url: 'https://www.yqueue.co/sg',
    description: 'Restaurant ordering system platform',
    icon: PhDeviceMobile,
  },
  {
    id: v4(),
    title: 'For Your Party',
    url: 'https://www.foryourparty.com/',
    description: 'Custom party supplies & stationery',
    icon: PhGift,
  },
  {
    id: v4(),
    title: 'Project Credo',
    url: 'https://projectcredo.com/',
    description: 'Scientific research platform',
    icon: PhTarget,
  },
  {
    id: v4(),
    title: 'TaskTribe',
    url: 'https://tasktribe.fi/',
    description: 'Task management & collaboration',
    icon: PhUsers,
  },
  {
    id: v4(),
    title: 'Azelab',
    url: 'https://azelab.com/',
    description: 'Digital solutions & consulting',
    icon: PhLightning,
  },
  {
    id: v4(),
    title: 'Laravel',
    url: 'https://laravel.com/',
    description: 'PHP web application framework',
    icon: PhCode,
  },
  {
    id: v4(),
    title: 'DataChef',
    url: 'https://www.datachef.com/',
    description: 'Data ops platform for revenue teams',
    icon: PhDatabase,
  },
  {
    id: v4(),
    title: 'TripExpert',
    url: 'https://www.tripexpert.com/',
    description: 'Trusted travel reviews & recommendations',
    icon: PhMapPin,
  },
])
</script>
